import LinkIcon from '@mui/icons-material/Link';
import LinkOffIcon from '@mui/icons-material/LinkOff';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import ShortTextIcon from '@mui/icons-material/ShortText';
import { Box, IconButton, InputBase, Popover } from '@mui/material';
import {
  isAllowedChatLinkHref,
  normalizeChatLinkHref,
} from 'components/@home/@messages/ChatPanel/SendForm/controls/ChatField/ChatInput/contentEditableSerializers';
import { bool, func, object, string } from 'prop-types';
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { I18n } from 'utils/i18n';

const rowSx = theme => ({
  display: 'flex',
  alignItems: 'center',
  gap: theme.spacing(1),
  padding: theme.spacing(0, 0.5),
});

const inputSx = {
  flex: 1,
  fontSize: 13,
};

const LinkPopover = ({
  anchorEl,
  open,
  onClose,
  onApply,
  onRemove,
  initialUrl = '',
  initialText = '',
  showRemove = false,
}) => {
  const [url, setUrl] = useState('');
  const [text, setText] = useState('');
  const [urlError, setUrlError] = useState(false);
  const urlInputRef = useRef(null);

  useEffect(() => {
    if (open) {
      setUrl(initialUrl || '');
      setText(initialText || '');
      setUrlError(false);
      requestAnimationFrame(() => {
        urlInputRef.current?.focus();
        urlInputRef.current?.select?.();
      });
    }
  }, [open, initialUrl, initialText]);

  const tryApply = useCallback(() => {
    const trimmed = String(url).trim();
    const href = normalizeChatLinkHref(trimmed);
    if (!isAllowedChatLinkHref(href)) {
      setUrlError(true);
      return;
    }
    setUrlError(false);
    onApply(href, text);
    onClose();
  }, [url, text, onApply, onClose]);

  const handleKeyDown = useCallback(
    e => {
      if (e.key === 'Escape') {
        e.stopPropagation();
        onClose();
      }
      if (e.key === 'Enter' && !e.shiftKey) {
        e.preventDefault();
        tryApply();
      }
    },
    [onClose, tryApply],
  );

  return (
    <Popover
      open={open}
      anchorEl={anchorEl}
      onClose={onClose}
      anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
      transformOrigin={{ vertical: 'top', horizontal: 'left' }}
      disableAutoFocus
      disableEnforceFocus
      elevation={0}
      marginThreshold={8}
      slotProps={{
        paper: {
          onKeyDown: handleKeyDown,
          sx: theme => ({
            padding: theme.spacing(1),
            minWidth: 280,
            maxWidth: 400,
            borderRadius: '8px',
            boxShadow: '0 4px 24px rgba(0,0,0,0.12)',
            border: `1px solid ${theme.palette.divider}`,
            display: 'flex',
            flexDirection: 'column',
          }),
        },
      }}
    >
      <Box sx={rowSx}>
        <ShortTextIcon fontSize="small" color="disabled" />
        <InputBase
          placeholder={I18n.t('SendForm.LinkText')}
          value={text}
          onChange={e => setText(e.target.value)}
          sx={inputSx}
          autoComplete="off"
          spellCheck="false"
        />
      </Box>
      <Box
        sx={theme => ({
          width: '100%',
          height: '1px',
          backgroundColor: theme.palette.divider,
          margin: theme.spacing(0.75, 0),
        })}
      />
      <Box sx={rowSx}>
        <LinkIcon fontSize="small" color="disabled" />
        <InputBase
          inputRef={urlInputRef}
          placeholder={I18n.t('SendForm.LinkURL')}
          value={url}
          onChange={e => {
            setUrl(e.target.value);
            setUrlError(false);
          }}
          sx={theme => ({
            ...inputSx,
            ...(urlError && { color: theme.palette.error.main }),
          })}
          autoComplete="off"
          spellCheck="false"
        />
        {showRemove ? (
          <>
            <Box
              sx={theme => ({
                width: '1px',
                height: 20,
                backgroundColor: theme.palette.divider,
                margin: theme.spacing(0, 0.5),
              })}
            />
            <IconButton
              size="small"
              onClick={() => window.open(initialUrl, '_blank', 'noopener,noreferrer')}
              aria-label={I18n.t('SendForm.OpenLink')}
              sx={theme => ({
                padding: '4px',
                color: theme.palette.text.secondary,
                '&:hover': {
                  color: theme.palette.primary.main,
                  backgroundColor: theme.palette.primary.ultraLight || 'rgba(98, 105, 229, 0.08)',
                },
              })}
            >
              <OpenInNewIcon style={{ fontSize: 16 }} />
            </IconButton>
            <IconButton
              size="small"
              onClick={onRemove}
              aria-label={I18n.t('SendForm.RemoveLink')}
              sx={theme => ({
                padding: '4px',
                color: theme.palette.text.secondary,
                '&:hover': {
                  color: theme.palette.error.main,
                  backgroundColor: theme.palette.error.ultraLight || 'rgba(222, 55, 48, 0.08)',
                },
              })}
            >
              <LinkOffIcon fontSize="small" />
            </IconButton>
          </>
        ) : null}
      </Box>
    </Popover>
  );
};

LinkPopover.propTypes = {
  anchorEl: object,
  open: bool.isRequired,
  onClose: func.isRequired,
  onApply: func.isRequired,
  onRemove: func,
  initialUrl: string,
  initialText: string,
  showRemove: bool,
};

export default LinkPopover;
